// Paid AI calls need a login (Phase 7, note 6). When the session ended while a form was open, the call answers
// 401 NO_SESSION or INVALID_SESSION. Instead of failing, ask the sign-in modal to open (the form stays mounted and
// filled), wait for the result, and send the same call again. Lookups that answer 429 are retried quietly.

export const SESSION_ENDED_CODES = ["NO_SESSION", "INVALID_SESSION"];
export const SESSION_ENDED_MESSAGE = "Your session has ended. Sign in again to continue. Your answers are kept.";

let sessionEndedHandler = null;

/** The sign-in modal registers itself here. The handler resolves true after a real sign-in, false if it was closed. */
export function setSessionEndedHandler(fn) {
  sessionEndedHandler = fn;
  return () => {
    if (sessionEndedHandler === fn) sessionEndedHandler = null;
  };
}

export async function readErrorCode(res) {
  try {
    const body = await res.clone().json();
    return typeof body?.code === "string" ? body.code : typeof body?.error === "string" ? body.error : null;
  } catch {
    return null;
  }
}

export async function isSessionEnded(res) {
  if (res.status !== 401) return false;
  const code = await readErrorCode(res);
  // A 401 with no readable code is still a missing login on these calls.
  return code === null || SESSION_ENDED_CODES.includes(code) || code === "Authentication required";
}

/**
 * `send` makes the request fresh each time (so it picks up the new login headers). On a session-ended 401 the sign-in
 * modal opens and, once signed in, `send` runs again. If the user closes the modal the 401 answer is returned as is.
 */
export async function fetchWithSignIn(send) {
  const res = await send();
  if (!(await isSessionEnded(res)) || !sessionEndedHandler) return res;
  const signedIn = await sessionEndedHandler();
  return signedIn ? send() : res;
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Retries quietly on 429 (a cheap lookup over its per minute limit): a short wait, at most `tries` more times. */
export async function fetchRetry429(send, { tries = 2, waitMs = 1500 } = {}) {
  let res = await send();
  for (let i = 0; i < tries && res.status === 429; i++) {
    const retryAfter = Number(res.headers.get("Retry-After"));
    await sleep(Number.isFinite(retryAfter) && retryAfter > 0 ? Math.min(retryAfter * 1000, 5000) : waitMs * (i + 1));
    res = await send();
  }
  return res;
}
